import { ModalHeader, ModalFooter, Button, Modal } from '@mfw/components';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import React from 'react';

import {
  InvitationActionEnum,
  type Invitation,
} from '../../schemas/invitation.schema';
import {
  ConnectionTermsOfUseConsent,
  TermsOfUseDisclaimer,
  TermsOfUseLoadState,
  getTermsOfUseQuestionTitle,
} from '../../allFeatures/TermsOfUse';
import { useUserMembershipTermsOfUse } from '../../hooks/memberships';
import useUpdateInvitation from '../../hooks/invitation/useUpdateInvitation';
import { useAuthContext } from '../../context';
import messages from './messages';

export interface InvitationModalProps {
  onClientOnboardingRequired?: () => void;
  invitation: Invitation;
  open: boolean;
}

const roleMessages = {
  assistant_trainer: messages.roleAssistantTrainer,
  client: messages.roleClient,
  organization_owner: messages.roleOrganizationOwner,
  trainer: messages.roleTrainer,
} as const;

const InvitationModal = ({
  invitation,
  onClientOnboardingRequired,
  open,
}: InvitationModalProps) => {
  const { formatMessage } = useIntl();
  const { currentUser } = useAuthContext();
  const { mutateAsync: updateInvitation, isPending: isSubmitting } =
    useUpdateInvitation({ onClientOnboardingRequired });
  const [acceptedVersionId, setAcceptedVersionId] = React.useState<
    string | null
  >(null);

  const [connectionVersionId, setConnectionVersionId] = React.useState<
    string | null
  >(null);

  const pendingMembership = invitation.pendingRecurringMembership;
  const connectionTerms = invitation.connectionTermsOfUse;
  const {
    data: termsOfUse,
    isPending: termsPending,
    isError: termsError,
  } = useUserMembershipTermsOfUse({
    clientId: currentUser?.id,
    enabled: Boolean(pendingMembership),
    organizationId: invitation.organizationId,
    userMembershipId: pendingMembership?.userMembershipId,
  });
  const answerByQuestionId = React.useMemo(
    () => new Map(termsOfUse?.answers.map((a) => [a.questionId, a.textValue])),
    [termsOfUse],
  );
  const accepted =
    Boolean(termsOfUse?.version) &&
    acceptedVersionId === termsOfUse?.version?.id;
  const connectionAccepted =
    !connectionTerms?.version ||
    connectionVersionId === connectionTerms.version.id;
  const acceptDisabled =
    isSubmitting ||
    (Boolean(pendingMembership) && !accepted) ||
    !connectionAccepted;

  if (!open) return null;
  // CLIENT invites come back with an empty roleKey, which rendered a dangling
  // "to join as a ." — fall back to a role-less sentence when there's no label.
  const roleLabel = invitation.roleKey
    ? invitation.roleKey in roleMessages
      ? formatMessage(
          roleMessages[invitation.roleKey as keyof typeof roleMessages],
        )
      : invitation.roleKey
    : '';

  const handleAcceptClick = () => {
    if (acceptDisabled) return;
    updateInvitation({
      organizationUserId: invitation.organizationUserId,
      touVersionId: pendingMembership ? termsOfUse?.version?.id : undefined,
      connectionTouVersionId: connectionTerms?.version?.id,
      type: InvitationActionEnum.Enum.accept,
    });
  };

  return (
    <Modal
      disableOverlayClose
      withoutCloseIcon
      initiallyOpen
      variant={pendingMembership || connectionTerms ? 'medium' : 'small'}
    >
      <ModalHeader>
        <Typography variant="h5">{formatMessage(messages.title)}</Typography>
      </ModalHeader>
      <Box display="flex" flexDirection="column" gap={3} py={3}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          gap={1}
        >
          <Typography variant="h6">
            {formatMessage(messages.textPartOne, {
              organizationName: invitation.organizationName,
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {roleLabel
              ? formatMessage(messages.textPartTwo, { role: roleLabel })
              : formatMessage(messages.textPartTwoNoRole)}
          </Typography>
        </Box>

        {connectionTerms?.version && (
          <ConnectionTermsOfUseConsent
            termsOfUse={connectionTerms}
            organizationName={invitation.organizationName}
            checkedVersionId={connectionVersionId}
            onChange={setConnectionVersionId}
            data-testid="invitation-connection-terms-accept-checkbox"
          />
        )}

        {pendingMembership && (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            p={2}
            border={1}
            borderColor="divider"
            borderRadius={2}
          >
            <Typography variant="subtitle1">
              {formatMessage(messages.termsOfUseTitle)}
            </Typography>
            {!termsOfUse?.version && (
              <TermsOfUseLoadState
                isPending={termsPending}
                isError={Boolean(termsError)}
                isUnavailable={Boolean(termsOfUse) && !termsOfUse?.version}
              />
            )}
            {termsOfUse?.version && (
              <Box display="flex" flexDirection="column" gap={2}>
                {termsOfUse.questions.map((question) => (
                  <Box key={question.id}>
                    <Typography variant="subtitle2">
                      {getTermsOfUseQuestionTitle(
                        {
                          questionKey: question.key,
                          questionText: question.questionText,
                        },
                        formatMessage,
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {answerByQuestionId.get(question.id)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {termsOfUse?.version && <TermsOfUseDisclaimer />}
            {termsOfUse?.version && (
              <FormControlLabel
                sx={{ alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    data-testid="invitation-terms-of-use-accept-checkbox"
                    checked={accepted}
                    onChange={(_e, checked) =>
                      setAcceptedVersionId(
                        checked ? (termsOfUse.version?.id ?? null) : null,
                      )
                    }
                    sx={{ pt: 0 }}
                  />
                }
                label={formatMessage(messages.termsOfUseAcceptLabel)}
              />
            )}
          </Box>
        )}
      </Box>

      <ModalFooter>
        <Button
          onClick={() =>
            updateInvitation({
              organizationUserId: invitation.organizationUserId,
              type: InvitationActionEnum.Enum.reject,
            })
          }
          data-testid="invitation-reject-button"
          disabled={isSubmitting}
          variant="outlined"
          size="medium"
        >
          {formatMessage(messages.reject)}
        </Button>
        <Button
          onClick={handleAcceptClick}
          data-testid="invitation-accept-button"
          disabled={acceptDisabled}
          variant="contained"
          sx={{ ml: 2 }}
          size="medium"
        >
          {formatMessage(messages.accept)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
export default InvitationModal;
